<script setup lang="ts">
/**
 * Встречи — страница «10 · Встречи» (макеты 10-1…10-7).
 *
 * Раскладка мессенджера: слева список встреч, справа заметки выбранной. Заметки
 * жили на плане и уезжали вниз всякий раз, когда список кандидатов оказывался
 * длинным (DEV-1969), — а превратились они за это время в инструмент созвона,
 * которому на странице «что я делаю сегодня» не место.
 *
 * Личные заметки — то же самое место, только без встречи: их пишут тем же
 * полем и в ту же ленту. Отдельная страница ради них означала бы два адреса, по
 * которым ищут одну и ту же мысль.
 *
 * Выбор живёт в адресе (`/meetings/:sessionId`), а не в состоянии экрана:
 * встречу пересылают ссылкой, и уведомление о приглашении ведёт прямо в неё.
 */
import { useToast } from '@vinprop/design-system'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import TrackerToolbar from '@/components/TrackerToolbar.vue'
import { useIsMobile } from '@/composables/useIsMobile'
import MeetingNotes from '@/features/meetings/components/MeetingNotes.vue'
import MeetingsList from '@/features/meetings/components/MeetingsList.vue'
import { useNotesStore } from '@/features/meetings/notesStore'
import { useMeetingStore } from '@/features/meetings/store'

import type { MeetingSession } from '@/features/meetings/api'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const toast = useToast()
const meetings = useMeetingStore()
const notes = useNotesStore()
const isMobile = useIsMobile()

/** Адрес личной ленты: она не встреча, но открывается тем же маршрутом */
const PERSONAL = 'personal'

/**
 * Что открыто: встреча, личные заметки или ничего.
 *
 * У личных свой адрес (`/meetings/personal`), а не пустой: на телефоне список и
 * лента — два разных экрана, и без адреса у ленты личные заметки с телефона
 * было бы не открыть вовсе.
 */
const opened = computed(() => String(route.params.sessionId ?? ''))

/** Встреча, чью ленту показываем; пусто — личные заметки */
const sessionId = computed(() => (opened.value === PERSONAL ? '' : opened.value))

const current = ref<MeetingSession | null>(null)

/**
 * Показать выбранную ленту.
 *
 * Поток событий открывается только у идущей встречи: у законченной событий не
 * будет никогда, и держать ради неё живое соединение значит ждать того, чего
 * не случится.
 */
const open = async (id: string) => {
  current.value = id ? await meetings.meetingById(id) : null

  if (current.value?.active) {
    meetings.openStream(id)
  } else {
    meetings.closeStream()
  }

  await notes.setSession(id)
}

const select = (id: string) => {
  void router.push(`/meetings/${id || PERSONAL}`)
}

/**
 * Назад к списку — только на телефоне: там это второй экран, а не колонка
 * рядом. Ведёт на `/meetings`, а не на личные заметки: «назад» из встречи в
 * чужую ленту читалось бы как переход, которого человек не просил.
 */
const backToList = () => {
  void router.push('/meetings')
}

onMounted(async () => {
  await Promise.all([meetings.refreshState(), meetings.loadMeetings()])

  // Пришёл на экран без адреса, а встреча идёт — открываем её: искать в списке
  // то, что происходит прямо сейчас, человек не должен.
  if (!opened.value && meetings.session) {
    meetings.consumePendingOpen()
    await router.replace(`/meetings/${meetings.session.id}`)

    return
  }

  // На широком экране лента стоит рядом со списком, и пустой она не бывает: не
  // выбрано ничего — значит открыты личные заметки.
  if (!opened.value && !isMobile.value) {
    await router.replace(`/meetings/${PERSONAL}`)

    return
  }

  await open(sessionId.value)
})

watch(opened, () => {
  void open(sessionId.value)
})

/**
 * Встреча сменилась — экран идёт за ней.
 *
 * Сменяется она не только концом: рождается собственным приглашением и
 * приходит чужим, на которое нажали в баннере. Все случаи — одно событие
 * «сессия стала другой», и разводить их по обработчикам значит гарантированно
 * забыть один: до DEV-1949 забыты были два.
 */
watch(
  () => meetings.session?.id ?? '',
  async (id, was) => {
    if (!id) {
      // Встречи не стало: автор закрыл или она протухла. Открытый архив
      // остаётся на экране — читать его можно и после конца.
      if (opened.value && current.value?.active) {
        toast.info(t('meetings.session.endedToast'))
        await meetings.loadMeetings()
        await open(sessionId.value)
      }

      return
    }

    const invited = meetings.consumePendingOpen()

    // Встреча появилась там, где её не было: либо позвали и человек вошёл,
    // либо он сам позвал кого-то из личных заметок и этим её начал. И то и
    // другое значит одно — писать теперь надо в неё, а не в личную ленту.
    if ((invited || !was) && opened.value !== id) {
      await meetings.loadMeetings()
      await router.push(`/meetings/${id}`)
    }
  },
)

/**
 * Уход с экрана отпускает встречу.
 *
 * Без этого сессия оставалась бы в сторе заметок: заметка, записанная «для
 * себя» с другого экрана, ушла бы в чужой мит (DEV-1947).
 */
onBeforeUnmount(() => {
  meetings.closeStream()
  void notes.setSession('')
})

/**
 * На телефоне колонки не помещаются рядом: сперва список, потом лента, назад —
 * стрелкой. Показываем ровно одно из двух.
 */
const showList = computed(() => !isMobile.value || !opened.value)
const showNotes = computed(() => !isMobile.value || !!opened.value)
</script>

<template>
  <TrackerToolbar :breadcrumbs="[{ label: t('nav.meetings') }]" />

  <div class="meetings" :class="{ 'meetings--mobile': isMobile }">
    <MeetingsList
      v-if="showList"
      :meetings="meetings.meetings"
      :selected-id="sessionId"
      :is-loading="meetings.isListLoading"
      @select="select"
    />

    <template v-if="showNotes">
      <button v-if="isMobile" type="button" class="meetings__back" @click="backToList()">
        ← {{ t('meetings.back') }}
      </button>

      <MeetingNotes :meeting="current" />
    </template>
  </div>
</template>

<style scoped>
.meetings {
  /* Ровно остаток экрана под тулбаром: скроллятся колонки, а не страница —
     длинный протокол не должен уводить список встреч за нижний край. */
  display: flex;
  gap: 20px;
  height: calc(100vh - 48px);
  overflow: hidden;
  padding: 20px 24px;
}

.meetings--mobile {
  flex-direction: column;
  gap: 12px;
  padding: 12px;
}

.meetings--mobile :deep(.mlist) {
  flex: 1;
  width: 100%;
  padding-right: 0;
  border-right: none;
}

.meetings__back {
  align-self: flex-start;
  padding: 4px 8px;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}
</style>
